import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MessageCircle, Loader2, Copy, Sparkles, PhoneCall, Megaphone, Target } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = {
  not_configured: { label: "Not Configured", badge: "bg-slate-100 text-slate-500" },
  configured: { label: "Configured", badge: "bg-amber-50 text-amber-600" },
  active: { label: "Active", badge: "bg-emerald-50 text-emerald-700" },
  error: { label: "Error", badge: "bg-red-50 text-red-600" },
};

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

export default function SettingsPage() {
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;
  const voiceWebhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/voice/incoming`;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ waba_id: "", phone_number_id: "", display_number: "", verify_token: "", access_token: "", is_active: false, ai_auto_response: true });
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState(null);
  const [vcfg, setVcfg] = useState(null);
  const [vform, setVform] = useState({ provider: "simulation", phone_number: "", account_sid: "", auth_token: "", human_agent_number: "", greeting: "", is_active: false, ai_auto_response: true });
  const [vsaving, setVsaving] = useState(false);
  const [mcfg, setMcfg] = useState(null);
  const [mform, setMform] = useState({ meta_ad_account_id: "", meta_access_token: "", meta_is_active: false, google_customer_id: "", google_developer_token: "", google_is_active: false });
  const [msaving, setMsaving] = useState(false);

  const saveAi = async (provider) => {
    try {
      const { data } = await api.put("/ai/config", { provider });
      setAi((a) => ({ ...a, provider: data.provider }));
      toast.success(`AI provider set to ${provider}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const load = async () => {
    const { data } = await api.get("/whatsapp/config");
    setCfg(data);
    setForm((f) => ({
      ...f,
      waba_id: data.waba_id || "", phone_number_id: data.phone_number_id || "",
      display_number: data.display_number || "", verify_token: data.verify_token || "",
      access_token: "", is_active: data.is_active, ai_auto_response: data.ai_auto_response,
    }));
    const { data: ai } = await api.get("/ai/config");
    setAi(ai);
    const { data: vdata } = await api.get("/voice/config");
    setVcfg(vdata);
    setVform((f) => ({
      ...f, provider: vdata.provider || "simulation", phone_number: vdata.phone_number || "",
      account_sid: vdata.account_sid || "", auth_token: "", human_agent_number: vdata.human_agent_number || "",
      greeting: vdata.greeting || "", is_active: vdata.is_active, ai_auto_response: vdata.ai_auto_response,
    }));
    const { data: mdata } = await api.get("/marketing/config");
    setMcfg(mdata);
    setMform((f) => ({
      ...f,
      meta_ad_account_id: mdata.meta.ad_account_id || "", meta_access_token: "", meta_is_active: mdata.meta.is_active,
      google_customer_id: mdata.google.customer_id || "", google_developer_token: "", google_is_active: mdata.google.is_active,
    }));
  };
  const saveMarketing = async () => {
    setMsaving(true);
    try {
      const payload = { ...mform };
      if (!payload.meta_access_token) delete payload.meta_access_token;
      if (!payload.google_developer_token) delete payload.google_developer_token;
      await api.put("/marketing/config", payload);
      toast.success("Marketing settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setMsaving(false); }
  };
  useEffect(() => { load(); }, []);

  const saveVoice = async () => {
    setVsaving(true);
    try {
      const payload = { ...vform };
      if (!payload.auth_token) delete payload.auth_token;
      await api.put("/voice/config", payload);
      toast.success("Voice settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setVsaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.access_token) delete payload.access_token; // keep existing token
      await api.put("/whatsapp/config", payload);
      toast.success("WhatsApp settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const st = STATUS[cfg?.status || "not_configured"];
  const vst = STATUS[vcfg?.status || "not_configured"];
  const metaSt = STATUS[mcfg?.meta?.status || "not_configured"];
  const googleSt = STATUS[mcfg?.google?.status || "not_configured"];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setv = (k) => (e) => setVform({ ...vform, [k]: e.target.value });
  const setm = (k) => (e) => setMform({ ...mform, [k]: e.target.value });

  return (
    <div className="animate-fade-up space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Organization & channel configuration</p>
      </div>

      <Card className="max-w-2xl shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base">
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp Business (Meta Cloud API)
          </CardTitle>
          {cfg && <Badge className={cn(st.badge)} data-testid="whatsapp-status">{st.label}</Badge>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-400">Webhook Callback URL</p>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 truncate text-xs text-slate-700">{webhookUrl}</code>
              <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }} data-testid="copy-webhook"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="WhatsApp Business Account ID"><Input value={form.waba_id} onChange={set("waba_id")} data-testid="wa-waba" /></Field>
            <Field label="Phone Number ID"><Input value={form.phone_number_id} onChange={set("phone_number_id")} data-testid="wa-phone-id" /></Field>
            <Field label="Business display number"><Input value={form.display_number} onChange={set("display_number")} data-testid="wa-display" placeholder="+91 ..." /></Field>
            <Field label="Webhook verify token"><Input value={form.verify_token} onChange={set("verify_token")} data-testid="wa-verify" /></Field>
            <Field label="Access token" hint={cfg?.has_token ? `Saved (${cfg.token_hint}). Leave blank to keep.` : "Stored securely; never shown after saving."}>
              <Input type="password" value={form.access_token} onChange={set("access_token")} data-testid="wa-token" placeholder={cfg?.has_token ? "••••••••" : "Paste token"} />
            </Field>
          </div>

          {cfg?.last_error && <p className="text-sm text-red-600">Last error: {cfg.last_error}</p>}

          <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="wa-active" /><span className="text-sm text-slate-700">Active</span></label>
              <label className="flex items-center gap-2"><Switch checked={form.ai_auto_response} onCheckedChange={(v) => setForm({ ...form, ai_auto_response: v })} data-testid="wa-ai" /><span className="text-sm text-slate-700">AI auto-response</span></label>
            </div>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </div>
          <p className="text-xs text-slate-400">Without valid Meta credentials, WhatsApp sending stays disabled — the AI Test simulator in Conversations continues to work.</p>
        </CardContent>
      </Card>

      <Card className="max-w-2xl shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base">
            <PhoneCall className="h-4 w-4 text-emerald-600" /> Voice AI / IVR (Telephony)
          </CardTitle>
          {vcfg && <Badge className={cn(vst.badge)} data-testid="voice-status">{vst.label}</Badge>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-400">Incoming Call Webhook URL (Twilio)</p>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 truncate text-xs text-slate-700">{voiceWebhookUrl}</code>
              <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(voiceWebhookUrl); toast.success("Copied"); }} data-testid="copy-voice-webhook"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>

          <Field label="Provider">
            <Select value={vform.provider} onValueChange={(v) => setVform({ ...vform, provider: v })}>
              <SelectTrigger data-testid="voice-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="simulation">Test / Simulation (no cost)</SelectItem>
                <SelectItem value="twilio">Twilio Programmable Voice</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Business phone number" hint="Resolves the organization for inbound calls"><Input value={vform.phone_number} onChange={setv("phone_number")} data-testid="voice-number" placeholder="+91 ..." /></Field>
            <Field label="Human agent number" hint="Used for transfer / handoff"><Input value={vform.human_agent_number} onChange={setv("human_agent_number")} data-testid="voice-agent" placeholder="+91 ..." /></Field>
            {vform.provider === "twilio" && <>
              <Field label="Twilio Account SID"><Input value={vform.account_sid} onChange={setv("account_sid")} data-testid="voice-sid" /></Field>
              <Field label="Twilio Auth Token" hint={vcfg?.has_token ? `Saved (${vcfg.token_hint}). Leave blank to keep.` : "Stored securely; never shown after saving."}>
                <Input type="password" value={vform.auth_token} onChange={setv("auth_token")} data-testid="voice-token" placeholder={vcfg?.has_token ? "••••••••" : "Paste token"} />
              </Field>
            </>}
          </div>
          <Field label="Greeting"><Input value={vform.greeting} onChange={setv("greeting")} data-testid="voice-greeting" placeholder="Hello, thank you for calling…" /></Field>

          {vcfg?.last_error && <p className="text-sm text-red-600">Last error: {vcfg.last_error}</p>}

          <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2"><Switch checked={vform.is_active} onCheckedChange={(v) => setVform({ ...vform, is_active: v })} data-testid="voice-active" /><span className="text-sm text-slate-700">Active</span></label>
              <label className="flex items-center gap-2"><Switch checked={vform.ai_auto_response} onCheckedChange={(v) => setVform({ ...vform, ai_auto_response: v })} data-testid="voice-ai" /><span className="text-sm text-slate-700">AI auto-response</span></label>
            </div>
            <Button onClick={saveVoice} disabled={vsaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="voice-save">{vsaving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </div>
          <p className="text-xs text-slate-400">Test mode runs the full Voice AI flow through the same orchestrator, knowledge base & scheduling with zero telephony cost — try it from the Calls page.</p>
        </CardContent>
      </Card>

      <Card className="max-w-2xl shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base">
            <Megaphone className="h-4 w-4 text-emerald-600" /> Meta Marketing (Facebook / Instagram Ads)
          </CardTitle>
          {mcfg && <Badge className={cn(metaSt.badge)} data-testid="meta-status">{metaSt.label}</Badge>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Ad Account ID"><Input value={mform.meta_ad_account_id} onChange={setm("meta_ad_account_id")} data-testid="meta-account" placeholder="act_123456789" /></Field>
            <Field label="Access Token" hint={mcfg?.meta?.has_token ? "Saved securely. Leave blank to keep." : "Stored server-side; never shown after saving."}>
              <Input type="password" value={mform.meta_access_token} onChange={setm("meta_access_token")} data-testid="meta-token" placeholder={mcfg?.meta?.has_token ? "••••••••" : "Paste token"} />
            </Field>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <label className="flex items-center gap-2"><Switch checked={mform.meta_is_active} onCheckedChange={(v) => setMform({ ...mform, meta_is_active: v })} data-testid="meta-active" /><span className="text-sm text-slate-700">Active</span></label>
            <Button onClick={saveMarketing} disabled={msaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="meta-save">{msaving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base">
            <Target className="h-4 w-4 text-emerald-600" /> Google Ads
          </CardTitle>
          {mcfg && <Badge className={cn(googleSt.badge)} data-testid="google-status">{googleSt.label}</Badge>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Customer ID"><Input value={mform.google_customer_id} onChange={setm("google_customer_id")} data-testid="google-account" placeholder="123-456-7890" /></Field>
            <Field label="Developer Token" hint={mcfg?.google?.has_token ? "Saved securely. Leave blank to keep." : "Stored server-side; never shown after saving."}>
              <Input type="password" value={mform.google_developer_token} onChange={setm("google_developer_token")} data-testid="google-token" placeholder={mcfg?.google?.has_token ? "••••••••" : "Paste token"} />
            </Field>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <label className="flex items-center gap-2"><Switch checked={mform.google_is_active} onCheckedChange={(v) => setMform({ ...mform, google_is_active: v })} data-testid="google-active" /><span className="text-sm text-slate-700">Active</span></label>
            <Button onClick={saveMarketing} disabled={msaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="google-save">{msaving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </div>
          <p className="text-xs text-slate-400">Credentials are stored server-side and never returned to the browser. Campaign spend sync activates once valid credentials are provided.</p>
        </CardContent>
      </Card>

      <Card className="max-w-2xl shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base">
            <Sparkles className="h-4 w-4 text-emerald-600" /> AI Provider
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">Choose which engine powers Wellora AI replies. All CRM & scheduling actions always run through Wellora's trusted logic.</p>
          <div className="flex items-center gap-3">
            <Select value={ai?.provider} onValueChange={saveAi}>
              <SelectTrigger className="w-56" data-testid="ai-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mock">Mock / Test</SelectItem>
                <SelectItem value="openai" disabled={!ai?.llm_key_configured}>OpenAI (GPT-4o-mini)</SelectItem>
                <SelectItem value="gemini" disabled={!ai?.llm_key_configured}>Gemini (2.0 Flash)</SelectItem>
              </SelectContent>
            </Select>
            <Badge className={ai?.llm_key_configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"} data-testid="ai-key-status">
              {ai?.llm_key_configured ? "LLM key configured" : "No LLM key"}
            </Badge>
          </div>
          <p className="text-xs text-slate-400">Keys are stored server-side and never shown. If a provider is unavailable, Wellora safely falls back to Mock.</p>
        </CardContent>
      </Card>
    </div>
  );
}
